import { zodResolver } from '@hookform/resolvers/zod';
import { mfaCodeSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/auth/useAuth';
import { ROUTES } from '@/constants/routes';
import type { MfaChallengeState, MfaSetupResult } from '@/features/identity/types/identity.types';
import type { LoginResult } from '@/types/auth.types';
import { parseRestError } from '@/utils/errorUtils';

const mfaChallengeSchema = z.object({ mfaCode: mfaCodeSchema });
type MfaChallengeFormValues = z.infer<typeof mfaChallengeSchema>;

/**
 * Drives `MfaChallengeForm`. Completing MFA replays the SAME authentication endpoint the user came
 * through (password / Google — there is no separate verify endpoint) with the original
 * primary credential plus the TOTP code, so password and OAuth behave identically (BR-2).
 *
 * Two modes, both handled here:
 *  • challenge — the account is already enrolled; the user just enters their code.
 *  • enrollment — a mandatory-MFA account has never enrolled; the server issued a pending secret
 *    (`enrollment`) to scan, and the same code submission both verifies and activates it. This is
 *    what closes the former lockout: an un-enrolled mandatory-MFA account can enroll inline at login
 *    instead of dead-ending (it can never reach the authenticated `/account/mfa/*` setup endpoints).
 */
export function useMfaChallenge() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = location.state as MfaChallengeState | null;
  const [serverError, setServerError] = useState<string | null>(null);
  // The pending secret to scan. Seeded from the first login response; refreshed if the server
  // re-issues one (e.g. the pending window lapsed before the code was entered).
  const [enrollment, setEnrollment] = useState<MfaSetupResult | undefined>(challenge?.enrollment);

  const form = useForm<MfaChallengeFormValues>({
    resolver: zodResolver(mfaChallengeSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { mfaCode: '' },
  });

  const resubmit = (mfaCode: string): Promise<LoginResult> => {
    switch (challenge?.method) {
      case 'password':
        return login({ email: challenge.email, password: challenge.password, mfaCode });
      case 'google':
        return loginWithGoogle(challenge.accessToken, mfaCode);
      default:
        return Promise.reject(new Error('No MFA challenge context'));
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (!challenge) return;
    setServerError(null);
    try {
      const result = await resubmit(values.mfaCode);
      if (result.tokens) {
        navigate(ROUTES.root, { replace: true });
        return;
      }
      // The pending enrollment lapsed and the server handed back a fresh secret — show the new QR.
      if (result.mfaEnrollmentRequired && result.mfaSetup) {
        setEnrollment(result.mfaSetup);
        form.reset({ mfaCode: '' });
        setServerError('That code expired. Scan the new QR code and enter a fresh code.');
      }
    } catch (error) {
      // Routed through `serverError` (an <Alert>), not `form.setError('mfaCode', ...)` — the shared
      // `OtpField` only shows a red border for a field error, never the message text.
      setServerError(parseRestError(error).message);
    }
  });

  return {
    form,
    onSubmit,
    serverError,
    enrollment,
    isEnrollment: Boolean(enrollment),
    hasChallengeContext: Boolean(challenge),
  };
}
